import type {
  AuthenticationStatus,
  AuthenticatorAssuranceLevel,
  Session,
  IdentityMetadataPublic,
  MfaMethod,
} from './session-adapter.type';

/**
 * Ory Session の便利I/F（Ory KratosのSessionオブジェクトを扱いやすくするAdapter）
 *
 * - MFA有効/無効の判定
 * - AAL判定
 * - allowed_methods / preferred_method の参照
 */
export class SessionAdapter {
  private readonly session: Session;

  constructor(session: Session) {
    this.session = session as Session;
  }

  /**
   * セッションIDを取得
   */
  get id(): string {
    return this.session.id;
  }

  /**
   * 認証保証レベル（AAL）を取得
   */
  get authenticatorAssuranceLevel(): AuthenticatorAssuranceLevel {
    return this.session
      .authenticator_assurance_level as AuthenticatorAssuranceLevel;
  }

  /**
   * セッションが有効かどうか
   */
  get isActive(): boolean {
    return this.session.active === true;
  }

  /**
   * MFAが有効かどうか
   *
   * metadata_public.mfa.enabled === true の場合にtrueを返します。
   */
  get isMfaEnabled(): boolean {
    const identity = this.session.identity;
    if (!identity) {
      return false;
    }

    const metadataPublic = identity.metadata_public as
      | IdentityMetadataPublic
      | undefined;
    return metadataPublic?.mfa?.enabled ?? false;
  }

  /**
   * AAL2まで認証済みかどうか
   */
  get isAal2Authenticated(): boolean {
    return this.authenticatorAssuranceLevel === 'aal2';
  }

  /**
   * AAL1まで認証済みかどうか
   *
   * AAL2はAAL1を含むため、AAL2の場合もtrueを返します。
   */
  get isAal1Authenticated(): boolean {
    return (
      this.authenticatorAssuranceLevel === 'aal1' ||
      this.authenticatorAssuranceLevel === 'aal2'
    );
  }

  /**
   * 認証状態を取得（MFA要件を考慮）
   *
   * ロジック:
   * - identityがない、またはAAL1未達成 → "unauthenticated"
   * - MFAが無効 → "authenticated"
   * - MFAが有効でAAL2達成 → "authenticated"
   * - MFAが有効でAAL1のみ → "requires_mfa"
   */
  get authenticationStatus(): AuthenticationStatus {
    // identityが無い場合は未認証扱い
    if (!this.session.identity) {
      return 'unauthenticated';
    }

    // AAL1すら達成していない場合は未認証
    if (!this.isAal1Authenticated) {
      return 'unauthenticated';
    }

    // MFAが無効な場合は認証済み
    if (!this.isMfaEnabled) {
      return 'authenticated';
    }

    // MFAが有効でAAL2達成済みなら認証済み
    if (this.isAal2Authenticated) {
      return 'authenticated';
    }

    // MFAが有効でAAL1のみの場合は追加認証が必要
    return 'requires_mfa';
  }

  /**
   * TOTPが登録されているかどうか
   *
   * authentication_methodsからTOTPの登録状態を確認します。
   */
  get hasTotp(): boolean {
    const authenticationMethods = this.session.authentication_methods || [];
    return authenticationMethods.some((method) => method.method === 'totp');
  }

  /**
   * メールMFA（コード送信）が登録されているかどうか
   *
   * authentication_methodsからcodeの登録状態を確認します。
   */
  get hasEmailCode(): boolean {
    const authenticationMethods = this.session.authentication_methods || [];
    return authenticationMethods.some((method) => method.method === 'code');
  }

  /**
   * 許可されているMFA方法のリストを取得
   *
   * metadata_public.mfa.allowed_methodsから取得します。
   * MFAが有効でない場合は空配列を返します。
   */
  get allowedMethods(): Array<MfaMethod> {
    const identity = this.session.identity;
    if (!identity) {
      return [];
    }

    const metadataPublic = identity.metadata_public as
      | IdentityMetadataPublic
      | undefined;
    return metadataPublic?.mfa?.allowed_methods ?? [];
  }

  /**
   * 推奨されるMFA方法（初期選択）
   *
   * metadata_public.mfa.preferred_methodから取得します。
   * 取得できない場合は、allowed_methods の先頭を返します。
   */
  get preferredMethod(): MfaMethod | null {
    const identity = this.session.identity;
    if (!identity) {
      return null;
    }

    const metadataPublic = identity.metadata_public as
      | IdentityMetadataPublic
      | undefined;
    const preferred = metadataPublic?.mfa?.preferred_method;

    if (preferred) {
      return preferred;
    }

    return this.allowedMethods[0] ?? null;
  }

  /**
   * 元のSessionオブジェクトを取得
   */
  get raw(): Session {
    return this.session;
  }

  /**
   * Sessionオブジェクトを取得（互換のため）
   */
  get extended(): Session {
    return this.session;
  }
}
